import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Race, Analysis, TeaserRace } from '../../types';
import { formatTipsPageUrl } from '../../utils/urlUtils';
import PrimePickBadge from '../ui/PrimePickBadge';

interface UpcomingTipsProps {
  races: Race[] | TeaserRace[];
  isAuthenticated?: boolean;
  showRegisterButton?: boolean;
}

// Type guard to check if it's teaser data
const isTeaserRace = (race: Race | TeaserRace): race is TeaserRace => {
  return !('analysis' in race);
};

const UpcomingTips: React.FC<UpcomingTipsProps> = ({ races, isAuthenticated = false, showRegisterButton = true }) => {
  const [now, setNow] = useState(new Date());
  const navigate = useNavigate();
  
  // Update the current time every second for the countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Format race time to local time
  const formatRaceTime = (raceTime: Date | string | undefined, trackState?: string): string => {
    if (!raceTime) return 'TBA';
    
    const date = new Date(raceTime);
    const localTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Add a note indicating this is local time
    return `${localTime} (local)`;
  };
  
  // Calculate time remaining until race
  const getTimeRemaining = (raceTime: Date | string | undefined, trackState?: string): { text: string; isUrgent: boolean } => {
    if (!raceTime) return { text: '', isUrgent: false };
    
    const date = new Date(raceTime);
    const diff = date.getTime() - now.getTime();
    
    // If race has started but within the 45-second buffer, still show "Now"
    // If race started more than 45 seconds ago, it will be filtered out by getUpcomingRaces
    if (diff <= 0) return { text: 'Now', isUrgent: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    // Check if less than 5 minutes remaining
    const isUrgent = hours === 0 && minutes < 5;
    
    let text = '';
    if (hours > 0) {
      text = `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      text = `${minutes}m ${seconds}s`;
    } else {
      text = `${seconds}s`;
    }
    
    return { text, isUrgent };
  };
  
  // Get the top pick for a race (only for authenticated Race objects)
  const getTopPick = (race: Race): Analysis | undefined => {
    if (!race.analysis || race.analysis.length === 0) return undefined;
    
    return race.analysis.find(a => a.pick_type === 'top' && a.pick_order === 1);
  };
  
  // Get the runner name for a pick (only for authenticated Race objects)
  const getRunnerName = (race: Race, horseId: string): string => {
    // First check if the analysis has the horse_name directly
    const topPick = getTopPick(race);
    if (topPick && topPick.horse_name) {
      return topPick.horse_name;
    }
    
    // Fallback to finding the runner by horse_id
    if (!race.runners || race.runners.length === 0) return 'Unknown';
    
    const runner = race.runners.find(r => r.tab_number.toString() === horseId);
    return runner ? runner.horse_name : 'Unknown';
  };
  
  // Filter races that have already started and get the next 2 upcoming races
  const getUpcomingRaces = (races: Race[] | TeaserRace[]): (Race | TeaserRace)[] => {
    if (!races || races.length === 0) return [];
    
    const now = new Date();
    
    // Filter out races that have already started, with a 45-second buffer
    // to account for races that don't start exactly on time
    const upcomingRaces = races.filter(race => {
      const raceTime = new Date(race.race_time || '');
      // Add 45 seconds (45000 milliseconds) to the race time before filtering it out
      const bufferedRaceTime = new Date(raceTime.getTime() + 45000);
      return bufferedRaceTime > now;
    });
    
    // Sort by start time (ascending)
    upcomingRaces.sort((a, b) => {
      const timeA = new Date(a.race_time || '').getTime();
      const timeB = new Date(b.race_time || '').getTime();
      return timeA - timeB;
    });
    
    // Return only the next 2 races
    return upcomingRaces.slice(0, 2);
  };
  
  // Handle click for teaser races
  const handleTeaserClick = (race: TeaserRace) => {
    navigate('/register', {
      state: {
        message: 'Sign up to see our racing analysis',
        returnTo: `/${race.meeting.track.state.toLowerCase()}/${race.meeting.track.code.toLowerCase()}/${race.race_id.split('-')[0]}`
      }
    });
  };
  
  // Get the upcoming races
  const upcomingRaces = getUpcomingRaces(races);
  
  if (!upcomingRaces || upcomingRaces.length === 0) {
    return null;
  }
  
  return (
    <div className="mb-2 w-full">
      <div className="flex items-center mb-2">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-racing-green" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-bold text-deep-navy">Upcoming Tips</span>
        </div>
        <div className="ml-2 h-px flex-grow bg-gradient-to-r from-racing-green to-transparent"></div>
      </div>
      
      <div className="flex flex-wrap gap-3">
        {upcomingRaces.map(race => {
          const timeRemaining = getTimeRemaining(race.race_time, race.meeting?.track?.state);
          const isUrgent = timeRemaining.isUrgent;
          
          if (isTeaserRace(race)) {
            // Render teaser version for unauthenticated users
            return (
              <div
                key={race.race_id}
                onClick={() => handleTeaserClick(race)}
                className={`flex-1 min-w-[250px] relative overflow-hidden rounded-md cursor-pointer transition-all duration-200 hover:shadow-md border-2 border-dashed border-racing-green/30 hover:border-racing-green bg-gradient-to-r from-racing-green/5 to-white hover:from-racing-green/10`}
              >
                {/* Left accent bar */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-racing-green"></div>
                
                <div className="flex justify-between items-center px-3 py-1 overflow-hidden">
                  <div className="flex items-center">
                    <div className="mr-2">
                      <div className="text-xs font-bold">{race.meeting?.track?.name} R{race.race_number}</div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="h-5 w-5 rounded-full bg-gray-400 text-white flex items-center justify-center mr-1">
                        <span className="text-xs">?</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        Tips available
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="text-xs text-gray-600 mr-1">{formatRaceTime(race.race_time, race.meeting?.track?.state).replace(' (local)', '')}</div>
                    <div className={`text-xs font-bold ${isUrgent ? 'text-bookie-red animate-pulse' : 'text-racing-green'}`}>
                      {timeRemaining.text}
                    </div>
                  </div>
                </div>
              </div>
            );
          } else {
            // Render full version for authenticated users
            const topPick = getTopPick(race);
            
            return (
              race.meeting?.track?.state && race.meeting?.track?.code && race.race_id ? (
                <Link
                  key={race.race_id}
                  to={{
                    pathname: formatTipsPageUrl(
                      race.meeting.track.state,
                      race.meeting.track.code,
                      race.race_id.split('-')[0]
                    ),
                    search: `?scrollToRace=${race.race_number}`
                  }}
                  className={`flex-1 min-w-[250px] relative overflow-hidden rounded-md cursor-pointer transition-all duration-200 hover:shadow-md ${
                    isUrgent 
                      ? 'bg-gradient-to-r from-bookie-red/10 to-white hover:from-bookie-red/20' 
                      : 'bg-gradient-to-r from-racing-green/10 to-white hover:from-racing-green/20'
                  }`}
                >
                  {/* Left accent bar */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                    isUrgent ? 'bg-bookie-red' : 'bg-racing-green'
                  }`}></div>
                  
                  <div className="flex justify-between items-center px-3 py-1 overflow-hidden">
                    <div className="flex items-center">
                      <div className="mr-2">
                        <div className="text-xs font-bold">{race.meeting?.track?.name} R{race.race_number}</div>
                      </div>
                      
                      {topPick && (
                        <div className="flex items-center">
                          <div className={`h-5 w-5 rounded-full flex items-center justify-center mr-1 ${
                            isUrgent ? 'bg-bookie-red text-white' : 'bg-racing-green text-white'
                          }`}>
                            <span className="text-xs font-bold">{topPick.horse_id}</span>
                          </div>
                          <div className="text-xs font-medium mr-1 max-w-[100px] truncate">{getRunnerName(race, topPick.horse_id)}</div>
                          {topPick.isConsensusPick && <PrimePickBadge iconOnly />}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center">
                      <div className="text-xs text-gray-600 mr-1">{formatRaceTime(race.race_time, race.meeting?.track?.state).replace(' (local)', '')}</div>
                      <div className={`text-xs font-bold ${isUrgent ? 'text-bookie-red animate-pulse' : 'text-racing-green'}`}>
                        {timeRemaining.text}
                      </div>
                    </div>
                  </div>
                </Link>
              ) : (
                <div key={race.race_id} className="flex-1 min-w-[250px] relative overflow-hidden rounded-md bg-gradient-to-r from-racing-green/5 to-white">
                  <div className="px-3 py-1.5">
                    <div className="text-xs text-gray-500">Race details unavailable</div>
                  </div>
                </div>
              )
            );
          }
        })}
      </div>
    </div>
  );
};

export default UpcomingTips;
